import { PALETTE } from "@eldrun/content";
import { BufferGeometry, Float32BufferAttribute, LineBasicMaterial, LineLoop, type Object3D } from "three";

export interface ArenaLike {
  x: number;
  z: number;
  radius: number;
}

const SEGMENTS = 96;
/** Altura do anel acima do terreno (m). */
const LIFT = 0.25;

/** Pontos do anel da arena, colados ao terreno. */
export function arenaPoints(a: ArenaLike, height: (x: number, z: number) => number): number[] {
  const out: number[] = [];
  for (let i = 0; i < SEGMENTS; i++) {
    const t = (i / SEGMENTS) * Math.PI * 2;
    const x = a.x + Math.cos(t) * a.radius;
    const z = a.z + Math.sin(t) * a.radius;
    out.push(x, height(x, z) + LIFT, z);
  }
  return out;
}

/** Anéis das arenas de Desafio (doc 06: arena marcada de 18 m), um por Pedra de Encanto em curso. */
export class ArenaRings {
  private readonly root: Object3D;
  private readonly height: (x: number, z: number) => number;
  private readonly material: LineBasicMaterial;
  private readonly rings = new Map<string, LineLoop>();

  constructor(root: Object3D, height: (x: number, z: number) => number, color: string = PALETTE.neutro.hex) {
    this.root = root;
    this.height = height;
    this.material = new LineBasicMaterial({ color, transparent: true, opacity: 0.85 });
  }

  sync(arenas: Iterable<[string, ArenaLike]>): void {
    const seen = new Set<string>();
    for (const [id, a] of arenas) {
      seen.add(id);
      if (this.rings.has(id)) continue;
      const geometry = new BufferGeometry();
      geometry.setAttribute("position", new Float32BufferAttribute(arenaPoints(a, this.height), 3));
      const ring = new LineLoop(geometry, this.material);
      ring.renderOrder = 4;
      this.root.add(ring);
      this.rings.set(id, ring);
    }
    for (const [id, ring] of this.rings) {
      if (seen.has(id)) continue;
      this.root.remove(ring);
      ring.geometry.dispose();
      this.rings.delete(id);
    }
  }

  get count(): number {
    return this.rings.size;
  }

  dispose(): void {
    this.sync([]);
    this.material.dispose();
  }
}
